import { useState, type ReactNode } from 'react';
import { Button, type ButtonVariant } from './Button.js';
import styles from './Controls.module.css';

export interface ConfirmButtonProps {
  label: ReactNode;
  /** What happens if they confirm, stated as impact. */
  message: ReactNode;
  confirmLabel?: ReactNode;
  variant?: ButtonVariant;
  size?: 'sm' | 'md';
  tone?: 'warning' | 'critical';
  disabled?: boolean;
  onConfirm: () => void | Promise<void>;
}

/**
 * Two-step action for anything hard to reverse. The confirmation is inline, states the
 * impact, and never uses a native dialog.
 */
export function ConfirmButton({ label, message, confirmLabel = 'Confirm', variant = 'secondary', size = 'sm', tone = 'warning', disabled, onConfirm }: ConfirmButtonProps) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!open) {
    return (
      <Button variant={variant} size={size} disabled={disabled} onClick={() => setOpen(true)}>
        {label}
      </Button>
    );
  }

  const confirm = async () => {
    setBusy(true);
    try {
      await onConfirm();
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={[styles.confirm, tone === 'critical' ? styles.confirmCritical : ''].filter(Boolean).join(' ')} role="alertdialog">
      <div>{message}</div>
      <div className={styles.actions}>
        <Button variant={tone === 'critical' ? 'destructive' : 'primary'} size="sm" disabled={busy} onClick={confirm}>
          {confirmLabel}
        </Button>
        <Button variant="secondary" size="sm" disabled={busy} onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

export interface SegmentedProps<T extends string> {
  options: ReadonlyArray<{ value: T; label: ReactNode }>;
  value: T;
  onChange: (value: T) => void;
  'aria-label'?: string;
}

/** Tab-like chip row with a hairline beneath. */
export function Segmented<T extends string>({ options, value, onChange, ...rest }: SegmentedProps<T>) {
  return (
    <div className={styles.segmented} role="tablist" aria-label={rest['aria-label']}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={o.value === value}
          className={[styles.chip, o.value === value ? styles.chipSelected : ''].filter(Boolean).join(' ')}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
